import { FC, ReactNode } from "react";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import { HighlightOff } from "@mui/icons-material";

import { Colors } from "../../../configs";

const LINE = "#DDE1E4";
const TEXT_STRONG = "#1B1D1F";

export interface IInfoDialogProps {
  open: boolean;
  title: string;
  /** Main body text. Strings honour "\n" line breaks. */
  message: ReactNode;
  /** Bold blue lead-in on the subtitle line, e.g. "Ticket #100482". */
  highlight?: ReactNode;
  /** Secondary subtitle text after the highlight, e.g. the customer name. */
  subtitle?: ReactNode;
  okLabel?: string;
  okIcon?: ReactNode;
  onOk(): void;
  /** Close (X), Escape and backdrop click. Falls back to onOk. */
  onClose?(): void;
  /** Extra body content rendered under the message. */
  children?: ReactNode;
  /** Replaces the default OK button for dialogs that need custom actions. */
  actions?: ReactNode;
  width?: string;
  testId?: string;
}

const InfoDialog: FC<IInfoDialogProps> = ({
  open,
  title,
  message,
  highlight,
  subtitle,
  okLabel = "OK",
  okIcon,
  onOk,
  onClose,
  children,
  actions,
  width = "40rem",
  testId = "info-dialog",
}) => {
  const handleClose = onClose ?? onOk;
  const titleId = `${testId}-title`;
  const messageId = `${testId}-message`;
  const hasSubtitle = !!highlight || !!subtitle;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={titleId}
      aria-describedby={messageId}
      data-testid={testId}
      BackdropProps={{ sx: { backgroundColor: "rgba(17, 20, 22, 0.55)" } }}
      PaperProps={{
        // On the Paper (which carries the labels), not the backdrop root
        role: "alertdialog",
        sx: {
          width: "100%",
          maxWidth: width,
          maxHeight: "90vh",
          m: "1.43rem",
          borderRadius: "1rem",
          overflow: "hidden",
          boxShadow: "0 1.7rem 4.3rem rgba(0, 0, 0, 0.28)",
          color: Colors.MAIN_GRAY,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "1.14rem",
          p: "1.43rem 1.43rem 1.14rem",
          borderBottom: `0.0714rem solid ${LINE}`,
        }}
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Typography
            id={titleId}
            component="h2"
            sx={{
              m: 0,
              fontSize: "var(--fs-lg)",
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
              color: TEXT_STRONG,
            }}
          >
            {title}
          </Typography>
          {hasSubtitle && (
            <Box
              sx={{
                mt: "0.36rem",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.5rem",
                fontSize: "var(--fs-2xs)",
                color: Colors.MAIN_GRAY,
              }}
            >
              {highlight && (
                <Box component="span" sx={{ fontWeight: 700, color: Colors.SECONDARY }}>
                  {highlight}
                </Box>
              )}
              {highlight && subtitle && (
                <Box
                  component="span"
                  sx={{
                    width: "0.22rem",
                    height: "0.22rem",
                    borderRadius: "50%",
                    background: "#C4C9CF",
                  }}
                />
              )}
              {subtitle && <span>{subtitle}</span>}
            </Box>
          )}
        </Box>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{
            flex: "0 0 auto",
            p: "0.3rem",
            color: Colors.MAIN_GRAY,
            "&:hover": { background: "#F1F3F5", color: TEXT_STRONG },
          }}
        >
          <HighlightOff sx={{ fontSize: "1.9rem" }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: "1.29rem 1.43rem", overflowY: "auto" }}>
        <Typography
          id={messageId}
          sx={{
            m: 0,
            fontSize: "var(--fs-xs)",
            lineHeight: 1.5,
            color: TEXT_STRONG,
            whiteSpace: "pre-line",
          }}
        >
          {message}
        </Typography>
        {children}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "0.57rem",
          p: "1rem 1.43rem 1.14rem",
          borderTop: `0.0714rem solid ${LINE}`,
          background: "#FBFCFC",
        }}
      >
        {actions ?? (
          <Button
            autoFocus
            disableElevation
            variant="contained"
            startIcon={okIcon}
            onClick={onOk}
            data-testid={`${testId}-ok`}
            sx={{
              minWidth: "8.6rem",
              height: "3rem",
              px: "1.29rem",
              borderRadius: "0.57rem",
              fontSize: "var(--fs-xs)",
              fontWeight: 700,
              textTransform: "none",
              background: Colors.PRIMARY,
              "&:hover": { background: Colors.PRIMARY_DARK },
            }}
          >
            {okLabel}
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default InfoDialog;
